import { Checkbox, FormControlLabel, Stack, Typography } from '@mui/material';
import {
  Chip,
  Dialog,
  DialogActionsWithButtons,
  DialogContent,
} from '@side/pantry';
import { useEffect, useRef, useState } from 'react';
import { ContactChangedFieldsList } from './ContactChangedFieldsList';
import { ContactChangeAction, type ContactChangeDetail } from './types';

function countContactsFields(contacts: ContactChangeDetail[]) {
  return contacts.reduce((acc, cur) => {
    // If the contact is not changed, do not count it's fields
    if (cur.action === ContactChangeAction.None) {
      return acc;
    }

    // For edited contacts, count the number of fields that have changed
    if (cur.action === ContactChangeAction.Edited) {
      return acc + cur.fields.length;
    }

    // For added and removed, consider it one change.
    return acc + 1;
  }, 0);
}

export const ContactChangeDialog = <TExtra = object>({
  contacts,
  open,
  title,
  onSubmit,
  onClose,
}: {
  contacts: ContactChangeDetail<TExtra>[];
  open: boolean;
  title: string;
  onSubmit: (contacts: ContactChangeDetail<TExtra>[]) => Promise<void>;
  onClose: () => void;
}) => {
  const [loading, setLoading] = useState(false);
  const [checkedContacts, setCheckedContacts] = useState<
    ContactChangeDetail<TExtra>[]
  >([]);
  const isInitialized = useRef(false);

  useEffect(() => {
    if (!isInitialized.current && open) {
      setCheckedContacts(contacts);
      isInitialized.current = true;
    }
  }, [contacts, open]);

  const fieldCount = countContactsFields(contacts);
  const chipLabel =
    fieldCount === 1 ? `${fieldCount} change` : `${fieldCount} changes`;

  if (!fieldCount) {
    return null;
  }

  const handleClose = () => {
    onClose();
    isInitialized.current = false;
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      await onSubmit(checkedContacts);
      handleClose();
    } catch {
      // The onSubmit function should handle errors and then bubble
      // an error up to the dialog so that the dialog will remain open.
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} maxWidth="sm" onClose={handleClose}>
      <DialogContent
        title={
          <Stack
            sx={{
              alignItems: 'center',
              gap: 2,
              flexDirection: 'row',
              justifyContent: 'space-between',
            }}
          >
            <Typography variant="h3">{title}</Typography>
            <Chip
              variant="outlined"
              color="warning"
              size="small"
              label={chipLabel}
            />
          </Stack>
        }
        onClose={handleClose}
      >
        <Typography sx={{ pb: 1.5, color: 'text.secondary' }}>
          Updating will override existing data on your transaction
        </Typography>
        {contacts.map((contact) => (
          <Stack sx={{ flexDirection: 'column' }} key={contact.id}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={checkedContacts.some((c) => c.id === contact.id)}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setCheckedContacts([...checkedContacts, contact]);
                    } else {
                      setCheckedContacts(
                        checkedContacts.filter((c) => c.id !== contact.id),
                      );
                    }
                  }}
                />
              }
              label={
                <Typography variant="subtitle2">{contact.label}</Typography>
              }
            />
            <ContactChangedFieldsList contact={contact} />
          </Stack>
        ))}
      </DialogContent>
      <DialogActionsWithButtons
        CancelButtonProps={{
          onClick: handleClose,
          disabled: loading,
        }}
        SubmitButtonProps={{
          defaultText: 'Accept Changes',
          loading,
          disabled: loading || !checkedContacts.length,
          color: 'warning',
          type: 'submit',
          onClick: () => {
            void handleSubmit();
          },
        }}
      />
    </Dialog>
  );
};
